'use client';
import AppLogo from '@/components/common/AppLogo';
import GlowIconButton from '@/components/common/GlowIconButton';
import HideOnScroll from '@/components/common/HideOnScroll';
import NavigationDrawer, { toggleDrawer } from '@/components/common/NavigationDrawer';
import InsightAppLogo from '@/components/proglove/InsightAppLogo';
import ProgloveLogo from '@/components/proglove/ProgloveLogo';
import { ProGlovePhase, useProGlove } from '@/components/proglove/ProgloveProvider';
import { useWebSocket } from '@/components/websocket/WebSocketProvider';
import { useSocketIO } from '@/components/socketio/SocketIOProvider';
import { MqttNamespace } from '@/context/messaging/mqtt';
import { useThemeBreakpoint } from '@/components/useThemeBreakpoint';
import useLogout from '@/components/common/authentication/useLogout';
import { sessionStateAtom } from '@/context/atoms/Session';
import { navigationIsOpen } from '@/context/atoms/navigation/NavigationIsOpen';
import { soundAtom } from '@/context/atoms/Sound';
import { ThemeMode, themeModeAtom } from '@/context/atoms/themeMode';
import { useAppModules } from '@/context/modules';
import {
  AccountCircle,
  AutorenewRounded,
  Circle,
  DarkModeOutlined,
  FluorescentRounded,
  Fullscreen,
  FullscreenExit,
  LightMode,
  Logout,
  Menu as MenuIcon,
  NavigateNext,
  Refresh,
  Sensors,
  SettingsBrightness,
  Star,
  TouchAppRounded,
  VolumeOff,
  VolumeUp,
} from '@mui/icons-material';
import {
  alpha,
  Backdrop,
  Badge,
  Breadcrumbs,
  Card,
  CardContent,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuList,
  Slide,
  Tooltip,
} from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled, useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAtom, useAtomValue } from 'jotai';
import { usePathname, useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

const getScopeInfo = (scope: MqttNamespace): { label: string; Icon: typeof TouchAppRounded } => {
  switch (scope) {
    case MqttNamespace.PICKING_PICKER_PROFILES:
      return { label: 'Picker', Icon: TouchAppRounded };
    case MqttNamespace.RECEIVING_AREAS:
      return { label: 'Ekanban', Icon: AutorenewRounded };
    case MqttNamespace.PICKING_ZONES:
      return { label: 'Zone', Icon: FluorescentRounded };
    default:
      return { label: 'Unknown', Icon: Sensors };
  }
};

export default function PrimaryAppBar(): React.JSX.Element {
  const [themeMode, setThemeMode] = useAtom(themeModeAtom);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [anchorElIndicator, setAnchorElIndicator] = React.useState<null | HTMLElement>(null);
  const min600 = useMediaQuery('(min-width: 600px)');
  const min480 = useMediaQuery('(min-width: 480px)');
  const theme = useTheme();
  const session = useAtomValue(sessionStateAtom);
  const [sound, setSound] = useAtom(soundAtom);
  const [isOpen, setOpen] = useAtom(navigationIsOpen);
  const [barTitle, setBarTitle] = useState('');

  const pathname = usePathname();
  const router = useRouter();
  const [title, setTitle] = useState<string | null>(null);
  const { getTitleByPathname } = useAppModules();

  const {
    isConnected: isSocketIOConnected,
    isConnecting: isSocketIOConnecting,
    isAcknowledged: isSocketIOAcknowledged,
    showIndicator: showSocketIOIndicator,
    connect: connectSocketIo,
    disconnect: disconnectSocketIo,
    subscriptions,
  } = useSocketIO();

  const isMenuOpen = Boolean(anchorEl);
  const isIndicatorMenuOpen = Boolean(anchorElIndicator);

  const hasProGloveConfig = true;
  const { phase } = useProGlove();
  const { connectedOnce } = useWebSocket();
  const tbp = useThemeBreakpoint();

  const handleProgloveNavigate = useCallback((): void => {
    setAnchorEl(null);
    setAnchorElIndicator(null);
    router.push('/proglove');
  }, [router]);

  // CONNECTING stays red like DISCONNECTED: nothing is reachable yet, and the
  // glow below is what signals that an attempt is running
  const proGloveStateColor = useMemo(() => {
    switch (phase) {
      case ProGlovePhase.READY:
        return theme.palette.success.main;
      case ProGlovePhase.NO_SCANNER:
        return theme.palette.info.main;
      default:
        return theme.palette.error.main;
    }
  }, [phase, theme.palette.success.main, theme.palette.info.main, theme.palette.error.main]);

  const titleTransitionCallback = useCallback(() => {
    setBarTitle(title ?? '');
  }, [title]);

  useEffect(() => {
    if (pathname !== null) {
      setTitle(getTitleByPathname(pathname));
    }
  }, [getTitleByPathname, pathname]);

  useEffect(() => {
    if (title !== null) {
      setBarTitle(title);
    }
  }, [title]);

  const handleFavorites = useCallback(
    (event: React.MouseEvent<HTMLElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      router.push('/favorites');
    },
    [router],
  );

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleIndicatorMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorElIndicator(event.currentTarget);
  };

  const handleMenuClose = (): void => {
    setAnchorEl(null);
  };

  const handleIndicatorMenuClose = (): void => {
    setAnchorElIndicator(null);
  };

  const logout = useLogout();

  const handleLogout = useCallback((): void => {
    void logout();
  }, [logout]);

  const fullscreenToggle = (): void => {
    if (document.fullscreenElement !== null) {
      void document.exitFullscreen();
    } else {
      void document.body.requestFullscreen();
    }
    setAnchorEl(null);
  };

  const soundToggle = useCallback((): void => {
    setSound({
      enabled: !sound.enabled,
      volume: sound.volume,
    });
  }, [sound, setSound]);

  const handleThemeMode = useCallback(
    (themeMode: ThemeMode) => (): void => {
      setThemeMode(themeMode);
    },
    [setThemeMode],
  );

  const subscriptionEntries = useMemo(() => Object.entries(subscriptions), [subscriptions]);

  const socketIndicatorColor = useMemo(() => {
    if (isSocketIOConnected && !isSocketIOConnecting && isSocketIOAcknowledged) {
      return theme.palette.success.main;
    }

    if (isSocketIOConnecting) {
      return theme.palette.info.main;
    }

    if (!isSocketIOAcknowledged) {
      return theme.palette.warning.main;
    }

    return theme.palette.error.main;
  }, [isSocketIOConnected, isSocketIOConnecting, isSocketIOAcknowledged, theme.palette]);

  const handleSocketIndicatorClick = useCallback(() => {
    // Always disconnect and reconnect - this will trigger resubscription automatically
    disconnectSocketIo();
    connectSocketIo();
  }, [connectSocketIo, disconnectSocketIo]);

  const socketSubscriptionsTooltip = useMemo(() => {
    const statusColor = isSocketIOConnected ? theme.palette.success.main : theme.palette.error.main;

    if (subscriptionEntries.length === 0) {
      return (
        <Box sx={{ p: 1.5, minWidth: 180 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Socket Status
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: statusColor,
              }}
            />
            <Typography variant="caption">{isSocketIOConnected ? 'Connected' : 'Disconnected'}</Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            No active subscriptions
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 1.5, minWidth: 240, maxWidth: 350 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Socket Status
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: statusColor,
            }}
          />
          <Typography variant="caption">{isSocketIOConnected ? 'Connected' : 'Disconnected'}</Typography>
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
          Active Subscriptions ({subscriptionEntries.length})
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {subscriptionEntries.map(([key, subscription]) => {
            const { label, Icon } = getScopeInfo(subscription.payload.namespace);
            const topicParts = subscription.payload.identifier.split('/').map((part) => decodeURIComponent(part));

            return (
              <Card
                key={key}
                variant="outlined"
                sx={{
                  backgroundColor: alpha(theme.palette.background.paper, 0.05),
                }}
              >
                <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    <Icon sx={{ fontSize: 14, color: 'common.white' }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'common.white' }}>
                      {label}
                    </Typography>
                  </Box>
                  <Breadcrumbs
                    separator={<NavigateNext sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }} />}
                    sx={{
                      '& .MuiBreadcrumbs-ol': {
                        flexWrap: 'wrap',
                      },
                      '& .MuiBreadcrumbs-separator': {
                        mx: 0.25,
                      },
                    }}
                  >
                    {topicParts.map((part, index) => (
                      <Typography
                        // eslint-disable-next-line @eslint-react/no-array-index-key -- MQTT topic breadcrumb; segments can repeat, no unique key
                        key={index}
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          color: index === topicParts.length - 1 ? 'common.white' : 'rgba(255,255,255,0.7)',
                          fontWeight: index === topicParts.length - 1 ? 'bold' : 'normal',
                        }}
                      >
                        {part}
                      </Typography>
                    ))}
                  </Breadcrumbs>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Box>
    );
  }, [
    subscriptionEntries,
    isSocketIOConnected,
    theme.palette.success.main,
    theme.palette.error.main,
    theme.palette.background.paper,
  ]);

  const indicators = useMemo(() => {
    if (!min480 && connectedOnce && showSocketIOIndicator) {
      // show both icons in the same icon button, one on the left bottom and one on the right top
      return (
        <IconButton
          size="large"
          edge="end"
          aria-label="indicators"
          onClick={handleIndicatorMenuOpen}
          color="inherit"
          sx={{ mr: tbp === 'xs' ? 1 : 2 }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: 24,
              width: 24,
            }}
          >
            <InsightAppLogo
              height={11}
              fill={proGloveStateColor}
              sx={{
                position: 'absolute',
                top: 15,
                left: 15,
                filter: `drop-shadow(0px 0px 2px ${proGloveStateColor})`,
              }}
            />
            <Sensors
              sx={{
                zoom: 0.7,
                position: 'absolute',
                bottom: 12,
                right: 12,
                color: isSocketIOConnected ? theme.palette.success.main : theme.palette.error.main,
                filter: isSocketIOConnected
                  ? `drop-shadow(0px 0px 2px ${theme.palette.success.main})`
                  : `drop-shadow(0px 0px 2px ${theme.palette.error.main})`,
              }}
            />
          </Box>
        </IconButton>
      );
    }

    return (
      <>
        {connectedOnce && (
          <GlowIconButton
            glow={phase === ProGlovePhase.DISCONNECTED || phase === ProGlovePhase.CONNECTING}
            size="large"
            edge="end"
            aria-label="proglove"
            onClick={handleProgloveNavigate}
            color="inherit"
            sx={{ mr: tbp === 'xs' ? 1 : 2 }}
            glowColor={
              phase === ProGlovePhase.CONNECTING
                ? alpha(theme.palette.info.main, 0.5)
                : alpha(theme.palette.error.main, 0.5)
            }
          >
            <InsightAppLogo
              height={20}
              fill={proGloveStateColor}
              sx={{ filter: `drop-shadow(0px 0px 2px ${proGloveStateColor})` }}
            />
          </GlowIconButton>
        )}
        {showSocketIOIndicator && (
          <Tooltip title={socketSubscriptionsTooltip} arrow placement="bottom">
            <GlowIconButton
              glow={!isSocketIOConnected}
              size="large"
              edge="end"
              aria-label="socket-io"
              onClick={handleSocketIndicatorClick}
              color="inherit"
              sx={{ mr: tbp === 'xs' ? 1 : 2 }}
              glowColor={isSocketIOConnecting ? alpha(theme.palette.info.main, 0.5) : alpha(socketIndicatorColor, 0.5)}
            >
              <Badge
                badgeContent={subscriptionEntries.length}
                invisible={subscriptionEntries.length === 0}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.4rem',
                  },
                }}
              >
                <Sensors
                  sx={{
                    color: socketIndicatorColor,
                    filter: `drop-shadow(0px 0px 2px ${socketIndicatorColor})`,
                    transition: 'color 0.25s ease',
                  }}
                />
              </Badge>
            </GlowIconButton>
          </Tooltip>
        )}
      </>
    );
  }, [
    phase,
    connectedOnce,
    socketIndicatorColor,
    handleProgloveNavigate,
    handleSocketIndicatorClick,
    isSocketIOConnected,
    isSocketIOConnecting,
    min480,
    proGloveStateColor,
    showSocketIOIndicator,
    socketSubscriptionsTooltip,
    subscriptionEntries.length,
    tbp,
    theme.palette.error.main,
    theme.palette.info.main,
    theme.palette.success.main,
  ]);

  const renderIndicatorMenu = useMemo(() => {
    return (
      <Menu
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        anchorEl={anchorElIndicator}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        id={'indicator-menu'}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        open={isIndicatorMenuOpen}
        onClose={handleIndicatorMenuClose}
      >
        <MenuItem onClick={handleProgloveNavigate}>
          <ListItemText disableTypography sx={{ display: 'flex', alignItems: 'center' }}>
            <ProgloveLogo height={16} />
          </ListItemText>
          <ListItemIcon sx={{ justifyContent: 'end' }}>
            <Circle sx={{ fontSize: 12, color: proGloveStateColor }} />
          </ListItemIcon>
        </MenuItem>
        <MenuItem onClick={connectSocketIo}>
          <ListItemText>Server</ListItemText>
          <ListItemIcon sx={{ justifyContent: 'end' }}>
            <Sensors sx={{ color: isSocketIOConnected ? theme.palette.success.main : theme.palette.error.main }} />
          </ListItemIcon>
        </MenuItem>
      </Menu>
    );
  }, [
    anchorElIndicator,
    isIndicatorMenuOpen,
    proGloveStateColor,
    handleProgloveNavigate,
    theme.palette.error.main,
    theme.palette.success.main,
    connectSocketIo,
    isSocketIOConnected,
  ]);

  const menuId = 'primary-search-account-menu';

  const renderMenu = useMemo(() => {
    return (
      <Menu
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        id={menuId}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={isMenuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem disabled={true}>
          <ListItemText>{session.user?.username}</ListItemText>
          <ListItemIcon sx={{ justifyContent: 'end' }}>
            <AccountCircle />
          </ListItemIcon>
        </MenuItem>
        <Divider sx={{ minWidth: 200, my: '0!important' }} />
        <MenuItem onClick={fullscreenToggle}>
          <ListItemText>Fullscreen</ListItemText>
          <ListItemIcon sx={{ justifyContent: 'end' }}>
            {document.fullscreenElement === null ? <Fullscreen /> : <FullscreenExit />}
          </ListItemIcon>
        </MenuItem>
        <MenuItem onClick={soundToggle}>
          <ListItemText>Sound</ListItemText>
          <ListItemIcon sx={{ justifyContent: 'end' }}>{sound.enabled ? <VolumeUp /> : <VolumeOff />}</ListItemIcon>
        </MenuItem>
        <MenuItem
          onClick={() => {
            window.location.reload();
          }}
        >
          <ListItemText>Reload App</ListItemText>
          <ListItemIcon sx={{ justifyContent: 'end' }}>
            <Refresh />
          </ListItemIcon>
        </MenuItem>
        <Divider sx={{ minWidth: 200, my: '0!important' }} />
        {hasProGloveConfig && (
          <MenuItem onClick={handleProgloveNavigate}>
            <ListItemText>ProGlove</ListItemText>
            {/* the Insight app icon doubles as the status light, same as in the app bar.
                ListItemIcon does not center a child without an explicit height, so the
                logo wrapper would otherwise sit at the top of the row */}
            <ListItemIcon sx={{ justifyContent: 'end', alignItems: 'center' }}>
              <InsightAppLogo height={20} fill={proGloveStateColor} />
            </ListItemIcon>
          </MenuItem>
        )}
        {hasProGloveConfig && <Divider sx={{ minWidth: 200, my: '0!important' }} />}
        <MenuItem dense disabled={true}>
          <ListItemText>Theme</ListItemText>
          <Typography variant="body2">{themeMode}</Typography>
        </MenuItem>
        <MenuList sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <MenuItem onClick={handleThemeMode(ThemeMode.LIGHT)}>
            <LightMode color={themeMode === ThemeMode.LIGHT ? 'secondary' : 'inherit'} />
          </MenuItem>
          <MenuItem onClick={handleThemeMode(ThemeMode.SYSTEM)}>
            <SettingsBrightness color={themeMode === ThemeMode.SYSTEM ? 'secondary' : 'inherit'} />
          </MenuItem>
          <MenuItem onClick={handleThemeMode(ThemeMode.DARK)}>
            <DarkModeOutlined color={themeMode === ThemeMode.DARK ? 'secondary' : 'inherit'} />
          </MenuItem>
        </MenuList>
        <Divider sx={{ minWidth: 200, my: '0!important' }} />
        <MenuItem onClick={handleLogout}>
          <ListItemText>Logout</ListItemText>
          <ListItemIcon sx={{ justifyContent: 'end' }}>
            <Logout />
          </ListItemIcon>
        </MenuItem>
      </Menu>
    );
  }, [
    anchorEl,
    isMenuOpen,
    session.user?.username,
    soundToggle,
    sound.enabled,
    themeMode,
    handleThemeMode,
    hasProGloveConfig,
    proGloveStateColor,
    handleProgloveNavigate,
    handleLogout,
  ]);

  const Offset = styled('div')(({ theme }) => theme.mixins.toolbar);

  return (
    <Box sx={{ height: [56, 64], flexGrow: 1, zIndex: (theme) => theme.zIndex.drawer - 1 }}>
      <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }} open={isMenuOpen} onClick={handleMenuClose} />
      <NavigationDrawer />
      <HideOnScroll>
        <AppBar position="fixed">
          <Toolbar>
            <IconButton
              size="large"
              edge="start"
              aria-label="open drawer"
              sx={{ mr: 2 }}
              onClick={toggleDrawer(!isOpen, setOpen)}
            >
              <MenuIcon sx={{ color: theme.palette.secondary.main }} />
            </IconButton>
            <AppLogo
              maxWidth={tbp === 'xs' && !min480 ? 68 : 92}
              fontSize={tbp === 'xs' && !min480 ? 38 : 56}
              forceSvgColor={'white'}
            />
            <Slide
              direction="down"
              in={title !== null}
              mountOnEnter
              unmountOnExit
              onEntering={titleTransitionCallback}
              onExited={titleTransitionCallback}
            >
              <Typography
                variant="h6"
                noWrap
                component="div"
                sx={{
                  fontWeight: 700,
                  fontSize: tbp === 'xs' && !min480 ? '18px' : '25px',
                  lineHeight: min600 ? '64px' : '55px',
                  alignSelf: 'end',
                  marginLeft: '8px',
                  display: { sm: 'block' },
                  color: theme.palette.secondary.main,
                }}
              >
                {barTitle}
              </Typography>
            </Slide>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
              {indicators}
              <IconButton
                size="large"
                edge="end"
                aria-label="favorites"
                onClick={handleFavorites}
                color="inherit"
                sx={{ mr: tbp === 'xs' ? 1 : 2 }}
              >
                <Star sx={{ color: theme.palette.secondary.main }} />
              </IconButton>
              <IconButton
                size="large"
                edge="end"
                aria-label="account of current user"
                aria-controls={menuId}
                aria-haspopup="true"
                onClick={handleProfileMenuOpen}
                color="inherit"
              >
                <AccountCircle sx={{ color: theme.palette.secondary.main }} />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      </HideOnScroll>
      <Offset />
      {renderMenu}
      {renderIndicatorMenu}
    </Box>
  );
}
